import React from 'react';
import { Card, Typography, Divider, Table, Collapse } from 'antd';
import { CaretRightOutlined } from '@ant-design/icons';
import BaseResultDisplay from './BaseResultDisplay';
import { ResultComponentProps } from './types';
import BioDataVisualization from '../../charts/BioDataVisualization';

const { Text, Title, Paragraph } = Typography;
const { Panel } = Collapse;

// 默认结果展示组件，用于处理没有专门组件的结果类型
const DefaultResultDisplay: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults
}) => {
  // 尝试自动生成表格数据
  const generateTableData = () => {
    if (!data) return null;
    
    // 如果数据是对象，尝试将其转换为表格
    if (typeof data === 'object' && !Array.isArray(data)) {
      const columns = [
        { title: '属性', dataIndex: 'key', key: 'key' },
        { title: '值', dataIndex: 'value', key: 'value' }
      ];
      
      const dataSource = Object.entries(data).map(([key, value], index) => {
        // 处理嵌套对象或数组
        let displayValue = value;
        if (typeof value === 'object' && value !== null) {
          displayValue = JSON.stringify(value);
        }
        
        return {
          key: index,
          prop: key,
          value: displayValue
        };
      });
      
      return { 
        columns: [
          { title: '属性', dataIndex: 'prop', key: 'prop' },
          { title: '值', dataIndex: 'value', key: 'value' }
        ], 
        dataSource 
      };
    }
    
    // 如果数据是数组且元素是对象，尝试将其作为表格数据
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      // 从第一个对象获取所有键作为列
      const firstItem = data[0];
      const columns = Object.keys(firstItem).map(key => ({
        title: key,
        dataIndex: key,
        key: key,
        render: (text: any) => {
          if (typeof text === 'object' && text !== null) {
            return JSON.stringify(text);
          }
          return text;
        }
      }));
      
      // 为每行添加key
      const dataSource = data.map((item, index) => ({
        key: index,
        ...item
      }));
      
      return { columns, dataSource };
    }
    
    return null;
  };
  
  // 尝试自动生成可视化数据
  const generateVisualization = () => {
    if (!data) return null;
    
    // 检查数据是否适合柱状图（简单数字数组或对象中的数字数组）
    if (Array.isArray(data) && data.every(item => typeof item === 'number')) {
      return {
        type: 'barChart',
        data: {
          xAxisData: Array.from({ length: data.length }, (_, i) => `项目 ${i+1}`),
          series: [{
            name: '值',
            data: data,
            color: '#1890ff'
          }]
        },
        title: '数据可视化',
        config: {}
      };
    }
    
    // 检查是否有键值对适合饼图
    if (typeof data === 'object' && !Array.isArray(data)) {
      const numericEntries = Object.entries(data).filter(([_, value]) => 
        typeof value === 'number'
      );
      
      if (numericEntries.length > 0) {
        return {
          type: 'pieChart',
          data: {
            seriesData: numericEntries.map(([name, value]) => ({
              name,
              value
            }))
          },
          title: '数据分布',
          config: {
            seriesName: '数据'
          }
        };
      }
    }
    
    return null;
  };
  
  // 生成JSON预览
  const renderJsonPreview = () => {
    if (!data) return <Text>无数据</Text>;
    
    try {
      const jsonString = JSON.stringify(data, null, 2);
      return (
        <pre style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '12px', 
          borderRadius: '4px',
          overflow: 'auto',
          maxHeight: '400px'
        }}>
          {jsonString}
        </pre>
      );
    } catch (error) {
      return <Text type="danger">无法解析数据为JSON格式</Text>;
    }
  };
  
  const tableData = generateTableData();
  const visualizationData = generateVisualization();
  
  return (
    <BaseResultDisplay
      data={data}
      title={title || "分析结果"}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
    >
      <Paragraph>
        <Text type="secondary">
          此类型的结果已自动生成可视化展示。如需更专业的展示，请联系开发团队添加专门的可视化组件。
        </Text>
      </Paragraph>
      
      <Divider />
      
      {/* 自动生成的可视化 */}
      {visualizationData && (
        <div style={{ marginBottom: 16 }}>
          <BioDataVisualization
            data={visualizationData.data}
            type={visualizationData.type as any}
            title={visualizationData.title}
            config={visualizationData.config}
          />
        </div>
      )}
      
      {/* 自动生成的表格 */}
      {tableData && (
        <div style={{ marginBottom: 16 }}>
          <Card title="数据表格" size="small">
            <Table
              columns={tableData.columns}
              dataSource={tableData.dataSource}
              pagination={tableData.dataSource.length > 10 ? { pageSize: 10 } : false}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </div>
      )}
      
      {/* 原始数据折叠面板 */}
      <Collapse
        bordered={false}
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
        style={{ background: '#f7f7f7', marginBottom: 24 }}
      >
        <Panel header="查看原始数据" key="1">
          {renderJsonPreview()}
        </Panel>
      </Collapse>
    </BaseResultDisplay>
  );
};

export default DefaultResultDisplay; 